import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Mail, Phone, ChartBar, Landmark, Target, Focus, Globe, TrendingUp, Briefcase, Users, TextQuote, Flag, Award, Star, TrendingDown, Clock, Building2, Map, BarChart, Home, ArrowUpRight, Scale, HandHeart } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import FlywheelDisplay from "@/components/onepager/FlywheelDisplay";
import ReturnsChart from "@/components/onepager/ReturnsChart";
import { LinkedinIcon } from "lucide-react";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import MustangFinancials from "@/components/onepager/MustangFinancials";
import CarBrandLogos from "@/components/onepager/CarBrandLogos";
import DealTimeline from "@/components/onepager/DealTimeline";
import ProjectMustangSection from '@/components/onepager/ProjectMustangSection';
import PDFViewer from '@/components/onepager/PDFViewer';
import AcquisitionStrategySection from "@/components/onepager/AcquisitionStrategySection";
import VisionSection from "@/components/onepager/VisionSection";
import MarketAndPipelineSection from "@/components/onepager/MarketAndPipelineSection";
import { Badge } from "@/components/ui/badge";

import { CarFront, Car, Hotel, Construction, Banknote, ArrowUp, BookText, FileText } from 'lucide-react';
import PostIntegrationPlaybookSection from "@/components/onepager/PostIntegrationPlaybookSection";
import { Card, CardContent } from "@/components/ui/card";
import ReturnsSection from "@/components/onepager/ReturnsSection";

const NOA_LOGO = "/lovable-uploads/e214b1a5-3e32-46c6-88b4-03c4d3444f26.png";

const TEAM_MEMBERS = [{
  name: "Hussein Fawzi",
  role: "CEO - Partner",
  company: "Noa Partners",
  img: "/lovable-uploads/325dbc11-b85f-4bbd-ac2b-23905415c5de.png",
  background: "Tech founder and operator; built Mexico's #2 largest restaurant VMS via an M&A roll-up."
}, {
  name: "Ronni Samir",
  role: "CIO - Partner",
  company: "Noa Partners",
  img: "/lovable-uploads/062e011a-6054-4e22-9bb8-3ef795f23d6e.png",
  background: "ETA with +10 years experience acquiring and building B2B Saas companies across LatAm."
}, {
  name: "Ignacio Lizarraga",
  role: "Chief Financial Officer",
  company: "Noa Partners",
  img: "/lovable-uploads/d67412b2-4dac-4f70-a82a-3c39286ebeaf.png",
  background: "Investor with 10+ years in M&A, private equity, and search funds across LatAm."
}];

const metrics = [{
  number: "5K+",
  label: "Total Market Size",
  tag: "LatAm"
}, {
  number: "1,300",
  label: "Initial Pipeline",
  tag: "Mexico"
}, {
  number: "35",
  label: "Active Discussions",
  tag: "Mexico"
}, {
  number: "7",
  label: "Active Deals",
  tag: "Mexico"
}];

const deals = [{
  company: "Project Mustang",
  status: "",
  arr: "$4.5M",
  ebitda: "$1.5M",
  price: "5x + EO",
  description: ["Dealership Management Software (DMS)", "for OEM car dealerships in Mexico."],
  logo: <CarFront className="w-5 h-5 text-blue-500" />
}, {
  company: "Project Phoenix",
  status: "",
  arr: "$5.5M",
  ebitda: "$3M",
  price: "5x + EO",
  description: ["Enterprise Resource Planner (ERP)", "for hotels in Mexico."],
  logo: <Hotel className="w-5 h-5 text-blue-500" />
}, {
  company: "Project Heron",
  status: "",
  arr: "$3M",
  ebitda: "$1M",
  price: "5x + EO",
  description: ["Core banking software", "for banks and financial institutions in Mexico."],
  logo: <Banknote className="w-5 h-5 text-blue-500" />
}, {
  company: "Project Condor",
  status: "",
  arr: "$9M",
  ebitda: "$2.5M",
  price: "6x + EO",
  description: ["Enterprise Resource Planner (ERP)", "for construction companies in Mexico."],
  logo: <Construction className="w-5 h-5 text-blue-500" />
}];

const CASE_STUDY = {
  vertical: "Restaurant Tech",
  productType: "Restaurant Management Software",
  ceoImage: "/lovable-uploads/325dbc11-b85f-4bbd-ac2b-23905415c5de.png",
  ceoQuote: "Noa Partners has been an invaluable partner in our growth journey. Their strategic guidance and operational expertise have helped us scale our business and achieve our goals.",
  ceoName: "Hussein Fawzi",
  ceoRole: "CEO, Ambit Technology"
};

const DeckPage: React.FC = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const totalSections = 11; // Updated to include the new section

  const goToNextSection = () => {
    setCurrentSection(prev => (prev + 1) % totalSections);
  };

  const goToPreviousSection = () => {
    setCurrentSection(prev => (prev - 1 + totalSections) % totalSections);
  };

  return (
    <div className="bg-white min-h-screen p-4 flex flex-col items-center justify-center">
      <div className="fixed top-4 right-4 flex items-center gap-2 z-50">
        <Button variant="outline" size="icon" onClick={goToPreviousSection} disabled={currentSection === 0} className="bg-white/80 backdrop-blur-sm">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="bg-white/80 backdrop-blur-sm px-3 py-1 rounded-md text-sm font-medium">
          {currentSection + 1} / {totalSections}
        </div>
        <Button variant="outline" size="icon" onClick={goToNextSection} disabled={currentSection === totalSections - 1} className="bg-white/80 backdrop-blur-sm">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-col items-center transition-transform duration-500 ease-in-out" style={{
      transform: `translateY(-${currentSection * 100}vh)`
    }}>
        
        <section className="w-[210mm] h-[297mm] bg-white shadow-lg mb-4 overflow-hidden flex flex-col">
          <div className="bg-gradient-to-br from-[#0D1F2D] to-[#1a2c3b] text-white p-12 h-full flex flex-col">
            <div className="flex justify-between items-start mb-16">
              <img src={NOA_LOGO} alt="Noa Partners Logo" className="h-6" />
              <div className="text-right">
                <p className="text-sm text-gray-400 uppercase tracking-wider">Confidential</p>
                <p className="text-sm text-gray-400">2025</p>
              </div>
            </div>

            <div className="flex-grow flex flex-col justify-center w-full">
              <div className="space-y-6 mb-16 w-full">
                <h1 className="text-5xl font-light leading-tight tracking-tight w-full">
                  Vertical Market Software <span className="text-blue-400 text-3xl align-super ml-1">(VMS)</span> Acquisition HoldCo.
                </h1>

                <Separator className="my-8 bg-blue-400/20 w-32" />

                <div className="flex flex-col justify-start items-start w-full">
                  <div className="border-l-4 border-blue-400 pl-4 space-y-4 w-full">
                    <p className="text-xl text-gray-300 leading-relaxed font-light w-full">Building Latin America's permanent VMS HoldCo—strategically acquiring, preserving, and scaling the region's top vertical software businesses.</p>
                    
                  </div>
                </div>
              </div>

              

              <div className="mt-auto pt-8">
                <Separator className="mb-8 bg-blue-400/20" />
                <div className="flex justify-between items-center text-sm text-gray-400">
                  <span>Noa Partners © 2025</span>
                  <span>www.noapartners.com</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        
        <section className="w-[210mm] h-[297mm] bg-white shadow-lg mb-4 overflow-hidden flex flex-col">
          <AcquisitionStrategySection />
        </section>

        
        <section className="w-[210mm] h-[297mm] bg-white shadow-lg mb-4 overflow-hidden flex flex-col">
          <div className="p-8 flex flex-col h-full space-y-6">
            <div className="relative">
              <div className="bg-gray-50/80 rounded-t-lg p-6">
                <div className="flex items-center gap-2 mb-4 text-left">
                  <ChartBar className="w-5 h-5 text-noa-paynes-gray" />
                  <h2 className="text-xl font-bold text-noa-paynes-gray">LatAm VMS Market Opportunity</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {metrics.map((metric, index) => (
                    <div key={index} className="bg-white shadow-md rounded-lg p-3 text-center hover:shadow-lg transition-shadow relative">
                      {metric.tag && <Badge variant="secondary" className="absolute right-[-8px] top-[-8px] text-xs bg-noa-rich-black/90 text-white">
                          {metric.tag === "Mexico" && <Flag className="w-3 h-3 mr-1" />}
                          {metric.tag}
                        </Badge>}
                      <div className="flex flex-col items-center justify-center">
                        <h3 className="text-xl font-bold text-noa-paynes-gray">{metric.number}</h3>
                        <p className="text-sm text-noa-charcoal">{metric.label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-100/90 rounded-b-lg p-6">
                <div className="text-left mb-3">
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-noa-paynes-gray" />
                    <h2 className="text-xl font-bold text-noa-rich-black text-left">
                      Highlighted Active Deals
                      <span className="text-xs font-light text-gray-500 ml-2">
                        all figures in USD assuming FX of $20 MXN/USD
                      </span>
                    </h2>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {deals.map((deal, index) => (
                    <div key={index} className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow border border-gray-200/50 relative">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-noa-rich-black">{deal.company}</h3>
                          {deal.logo && <div>{deal.logo}</div>}
                        </div>
                        {deal.status && <span className="px-2 py-1 bg-noa-antiflash-white text-noa-paynes-gray rounded-full text-xs">
                            {deal.status}
                          </span>}
                      </div>
                      {Array.isArray(deal.description) ? (
                        <p className="text-noa-charcoal text-xs mb-2 italic">
                          {deal.description[0]}<br />
                          {deal.description[1]}
                        </p>
                      ) : (
                        <p className="text-noa-charcoal text-xs mb-2 italic">{deal.description}</p>
                      )}
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-noa-charcoal"><strong>ARR:</strong> {deal.arr}</span>
                        <span className="text-noa-charcoal"><strong>EBITDA:</strong> {deal.ebitda}</span>
                        <span className="text-noa-charcoal"><strong>Price:</strong> {deal.price}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="text-left mt-6">
              <div className="flex items-center justify-between mb-4 px-6">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-noa-paynes-gray" />
                  <h2 className="text-xl font-bold text-noa-rich-black text-left">
                    Our Most Advanced Deal
                  </h2>
                  <Badge variant="secondary" className="bg-green-500 text-white text-xs ml-2">
                    Under Exclusivity & DD
                  </Badge>
                </div>
              </div>
              <p className="text-noa-charcoal text-sm mb-2 text-left px-6">
                Project Mustang — a Dealership Management Software (DMS) with 33% market share in Mexico's OEM car dealership market, with over 20 years in the industry.
              </p>
              <div className="rounded-lg overflow-hidden shadow-lg max-h-[400px] px-6">
                <img src="/lovable-uploads/5f6d9c67-e6a8-4b7b-a781-a408bbb0f36b.png" alt="Project Mustang Overview" className="w-full h-full object-contain" />
              </div>
            </div>
          </div>
        </section>

        
        <section className="w-[210mm] h-[297mm] bg-white shadow-lg mb-4 overflow-hidden flex flex-col">
          <div className="p-12 h-full">
            <div className="space-y-16">
              <div>
                <h2 className="text-3xl font-bold text-noa-rich-black mb-4">Noa Partners - Our Value Proposition</h2>
                <h3 className="text-xl font-medium text-gray-700 mb-6">Why Owners Choose Noa Partners:</h3>
                
                <div className="grid grid-cols-3 gap-8 mb-12">
                  <Card className="border border-gray-200 hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-blue-500/10 p-3 rounded-full">
                          <Home className="h-6 w-6 text-blue-500" />
                        </div>
                        <h3 className="text-xl font-semibold text-noa-rich-black">Buy, Hold & Grow Long-Term</h3>
                      </div>
                      <p className="text-gray-600">
                        We offer a permanent home—not a flip. Founders trust us to preserve their legacy while driving long-term growth.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border border-gray-200 hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-blue-500/10 p-3 rounded-full">
                          <TrendingUp className="h-6 w-6 text-blue-500" />
                        </div>
                        <h3 className="text-xl font-semibold text-noa-rich-black">Strategic Support to Scale</h3>
                      </div>
                      <p className="text-gray-600">
                        We provide more than capital—access to playbooks, talent, and shared resources to accelerate growth.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border border-gray-200 hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-blue-500/10 p-3 rounded-full">
                          <Users className="h-6 w-6 text-blue-500" />
                        </div>
                        <h3 className="text-xl font-semibold text-noa-rich-black">Decentralized by Design</h3>
                      </div>
                      <p className="text-gray-600">
                        Operators stay in control. We back strong teams and support them with strategic guidance—not interference.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        
        <section className="w-[210mm] h-[297mm] bg-white shadow-lg mb-4 overflow-hidden flex flex-col">
          <div className="p-12 h-full">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-noa-rich-black">How We Build Long-Term Value</h2>
              <Separator className="mt-4 bg-gray-200" />
            </div>
            
            <div className="space-y-10">
              {/* 1. Our Operating Model */}
              <div>
                <h3 className="text-xl font-semibold text-noa-rich-black mb-4 bg-blue-50 py-2 px-4 rounded-lg inline-block">1. Our Operating Model</h3>
                
                <div className="grid grid-cols-3 gap-6">
                  <Card className="border border-gray-200 hover:shadow-lg transition-all">
                    <CardContent className="p-5">
                      <h4 className="text-lg font-semibold text-noa-rich-black mb-2">Bet on Entrepreneurs</h4>
                      <p className="text-gray-600 text-sm">
                        Flexible transition plans for founders who want to stay, step back, or hand over leadership—with aligned incentives and optional equity rollover.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border border-gray-200 hover:shadow-lg transition-all">
                    <CardContent className="p-5">
                      <h4 className="text-lg font-semibold text-noa-rich-black mb-2">Decentralized Management</h4>
                      <p className="text-gray-600 text-sm">
                        Portfolio companies remain autonomous; local leaders operate independently with strategic support from Noa.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border border-gray-200 hover:shadow-lg transition-all">
                    <CardContent className="p-5">
                      <h4 className="text-lg font-semibold text-noa-rich-black mb-2">Resources for Growth</h4>
                      <p className="text-gray-600 text-sm">
                        Access to Noa's shared services platform: talent, capital, and battle-tested growth frameworks.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
              
              <Separator className="bg-gray-200" />
              
              {/* 2. A Platform of Platforms */}
              <div>
                <h3 className="text-xl font-semibold text-noa-rich-black mb-4 bg-blue-50 py-2 px-4 rounded-lg inline-block">2. A Platform of Platforms</h3>
                
                <div className="grid grid-cols-3 gap-6">
                  <Card className="border border-gray-200 hover:shadow-lg transition-all">
                    <CardContent className="p-5">
                      <h4 className="text-lg font-semibold text-noa-rich-black mb-2">Industry-Led Thesis</h4>
                      <p className="text-gray-600 text-sm">
                        We acquire market leaders in essential industries—high-retention, high-share assets that serve as future platform companies.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border border-gray-200 hover:shadow-lg transition-all">
                    <CardContent className="p-5">
                      <h4 className="text-lg font-semibold text-noa-rich-black mb-2">Tailwinds & Consolidation</h4>
                      <p className="text-gray-600 text-sm">
                        Focused on verticals undergoing tech transformation with fragmented markets and clear roll-up potential.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border border-gray-200 hover:shadow-lg transition-all">
                    <CardContent className="p-5">
                      <h4 className="text-lg font-semibold text-noa-rich-black mb-2">Essential Industries Focus</h4>
                      <p className="text-gray-600 text-sm">
                        Automotive, Construction, Healthcare, Education, Logistics, and other $100B+ analog sectors.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
              
              <Separator className="bg-gray-200" />
              
              {/* 3. Noa Shared Services Platform */}
              <div>
                <h3 className="text-xl font-semibold text-noa-rich-black mb-4 bg-blue-50 py-2 px-4 rounded-lg inline-block">3. Noa Shared Services Platform</h3>
                
                <div>
                  <Card className="border border-gray-200 hover:shadow-lg transition-all">
                    <CardContent className="p-5">
                      <p className="text-gray-600 font-medium mb-3">
                        Built to accelerate growth and preserve margin across the portfolio:
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center">
                          <div className="h-2 w-2 bg-blue-500 rounded-full mr-2"></div>
                          <span className="text-sm">Talent Acquisition & Executive Search</span>
                        </div>
                        <div className="flex items-center">
                          <div className="h-2 w-2 bg-blue-500 rounded-full mr-2"></div>
                          <span className="text-sm">GTM & Sales Playbooks</span>
                        </div>
                        <div className="flex items-center">
                          <div className="h-2 w-2 bg-blue-500 rounded-full mr-2"></div>
                          <span className="text-sm">Pricing & Monetization Strategy</span>
                        </div>
                        <div className="flex items-center">
                          <div className="h-2 w-2 bg-blue-500 rounded-full mr-2"></div>
                          <span className="text-sm">Technology, AI & DevOps Enablement</span>
                        </div>
                        <div className="flex items-center">
                          <div className="h-2 w-2 bg-blue-500 rounded-full mr-2"></div>
                          <span className="text-sm">Financial Planning & Analysis (FP&A)</span>
                        </div>
                        <div className="flex items-center">
                          <div className="h-2 w-2 bg-blue-500 rounded-full mr-2"></div>
                          <span className="text-sm">M&A Support – Add-On Acquisition Execution</span>
                        </div>
                        <div className="flex items-center">
                          <div className="h-2 w-2 bg-blue-500 rounded-full mr-2"></div>
                          <span className="text-sm">Embedded Fintech Layer & Monetization Tools</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        
        <section className="w-[210mm] h-[297mm] bg-white shadow-lg mb-4 overflow-hidden flex flex-col">
          <div className="p-12 h-full">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-noa-rich-black">The Last Great Frontier for VMS Consolidation: LatAm's Inflection Moment</h2>
              <Separator className="mt-4 bg-gray-200" />
            </div>
            
            <div className="mb-16">
              <h3 className="text-xl font-semibold text-noa-rich-black mb-2">
                <span className="bg-blue-500 text-white px-2 py-1 rounded mr-1">Past</span> Global Acquirers Stayed Away
              </h3>
              <h3 className="text-xl font-semibold text-noa-rich-black mb-6">— High Prices, Loose Governance, Complex Deals</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                <Card className="bg-gray-50 border-gray-200 hover:shadow-md transition-shadow duration-300">
                  <CardContent className="pt-5 p-5">
                    <h4 className="text-base font-bold text-noa-rich-black mb-2">Governance Gaps in LatAm</h4>
                    <p className="text-sm text-gray-600">Informality, tax schemes, and weak financials increased deal risk.</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gray-50 border-gray-200 hover:shadow-md transition-shadow duration-300">
                  <CardContent className="pt-5 p-5">
                    <h4 className="text-base font-bold text-noa-rich-black mb-2"> Opaque Market</h4>
                    <p className="text-sm text-gray-600">
                      Informality, tax schemes, and weak financials increased deal risk.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gray-50 border-gray-200 hover:shadow-md transition-shadow duration-300">
                  <CardContent className="pt-5 p-5">
                    <h4 className="text-base font-bold text-noa-rich-black mb-2">High Valuation Expectations</h4>
                    <p className="text-sm text-gray-600">
                      Sellers demanded high multiples, ignoring adjusted free cash flow.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gray-50 border-gray-200 hover:shadow-md transition-shadow duration-300">
                  <CardContent className="pt-5 p-5">
                    <h4 className="text-base font-bold text-noa-rich-black mb-2">Founders Not Ready to Exit</h4>
                    <p className="text-sm text-gray-600">
                      Most (in their 40s–50s) were still scaling—exit was not top of mind.
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              
              <div className="bg-red-50 border border-red-200 p-3 rounded-lg w-full text-center mb-6">
                <p className="text-base font-semibold text-red-800">LatAm Inflection Point — Conditions Now Favor Consolidation</p>
              </div>
              
              
              <div className="mt-10">
                <h3 className="text-xl font-semibold text-noa-rich-black mb-2">
                  <span className="bg-blue-500 text-white px-2 py-1 rounded mr-1">Now</span> Growing Base of Acquisition-Ready Targets
                </h3>
                <h3 className="text-xl font-semibold text-noa-rich-black mb-6">— Clean Books, Committed Sellers, Attractive Multiples</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                  <Card className="bg-blue-50/50 border-blue-200 hover:shadow-md transition-shadow duration-300">
                    <CardContent className="pt-5 p-5">
                      <h4 className="text-base font-bold text-noa-rich-black mb-2">Stricter Tax & Legal Enforcement</h4>
                      <p className="text-sm text-gray-600">
                        Governments are formalizing books and aligning reporting to GAAP/IFRS.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-blue-50/50 border-blue-200 hover:shadow-md transition-shadow duration-300">
                    <CardContent className="pt-5 p-5">
                      <h4 className="text-base font-bold text-noa-rich-black mb-2">Aging Founders — Exit Ready</h4>
                      <p className="text-sm text-gray-600">
                        First-gen VMS owners are entering retirement and succession phase.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-blue-50/50 border-blue-200 hover:shadow-md transition-shadow duration-300">
                    <CardContent className="pt-5 p-5">
                      <h4 className="text-base font-bold text-noa-rich-black mb-2">Post-COVID Digitization Boom</h4>
                      <p className="text-sm text-gray-600">
                        SaaS adoption surged; many vendors now at $2–5M ARR with solid infrastructure.
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-blue-50/50 border-blue-200 hover:shadow-md transition-shadow duration-300">
                    <CardContent className="pt-5 p-5">
                      <h4 className="text-base font-bold text-noa-rich-black mb-2">Valuations Now Reasonable</h4>
                      <p className="text-sm text-gray-600">5–7x EBITDA multiples; fewer sellers anchor to inflated tax-optimized cash flow.
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="bg-green-50 border border-green-200 p-3 rounded-lg w-full text-center">
                  <h4 className="text-base font-semibold text-green-800 mb-0">Sellers Prefer Local Buyers — In LatAm, Trust Beats Global Capital
                </h4>
                  
                </div>
              </div>
            </div>
          </div>
        </section>

        
        <section className="w-[210mm] h-[297mm] bg-white shadow-lg mb-4 overflow-hidden flex flex-col">
          <VisionSection />
        </section>

        
        <section className="w-[210mm] h-[297mm] bg-white shadow-lg mb-4 overflow-hidden flex flex-col">
          <ReturnsSection />
        </section>

        <section className="w-[210mm] h-[297mm] bg-white shadow-lg mb-4 overflow-hidden flex flex-col">
          <div className="p-12 h-full">
            <h2 className="text-3xl font-bold text-noa-rich-black mb-8">Team</h2>
            {/* Team content would go here */}
          </div>
        </section>

      </div>
    </div>
  );
};

export default DeckPage;
